import { incompatible, nameParts, normalizeOcrItemText } from "./ocrItemCompatibility";

const text = (value) => String(value ?? "").trim();
export const ocrItemAliasKey = (value) => normalizeOcrItemText(ocrDescriptionForItemMatch(value));
const referenceKey = (value) => text(value).toUpperCase();
const reliableSources = new Set(["exact_name", "supplier_code", "internal_code", "fuzzy", "learning"]);

// Keep product wording separate from explicitly labelled values printed for this delivery.
const expiryLabel = String.raw`(?:(?:(?:fecha|data)\s+(?:(?:de|di)\s+)?)?(?:caducidad|caducitat|cad|expiraci[oó]n|expiration|expiry|exp|consumo\s+preferente|consum\s+preferent|scadenza)|(?:fecha|data)\s+(?:de\s+)?vencimiento|use[\s-]+by|best[\s-]+before|date\s+de\s+p[ée]remption)(?:\s+date)?`;
const printedDate = String.raw`(\d{4}[-/.]\d{1,2}[-/.]\d{1,2}|\d{1,2}[-/.]\d{1,2}[-/.](?:\d{4}|\d{2}))`;
const unit = String.raw`(?:kg|g|gr|l|ml|cl|uds?|unds?|u|unidad(?:es)?|unitats?|units?|pcs?|cjs?|cajas?|caixas?|caixes|boxes|box)`;
const amount = String.raw`\d+(?:[.,]\d+)?\s*${unit}\.?`;
const lot = String.raw`(?:lote|lotto|lot|batch)\s*(?:[:=#]|n[º°]|no\.)\s*(?!\d+(?:[.,]\d+)?\s*(?:${unit}|botellas?|bottles?|x\s*\d+)\b)(?=[\w./-]{0,63}\d)[a-z0-9][\w./-]{0,63}`;
const delivered = String.raw`(?:cdad|cant(?:idad)?|quantitat|qty|quantity)\.?\s*[:=]\s*${amount}(?:\s+${amount})*`;
const traceField = new RegExp(String.raw`(^|[\s,;|])(?:${expiryLabel}(?:\s*[:=]\s*|\.\s*[:=]?\s*|\s+)${printedDate}\.?|${lot}|${delivered})(?=$|[\s,;|])`, "gi");

function validPrintedDate(value) {
  const [first, month, last] = value.split(/[-/.]/);
  const iso = first.length === 4;
  const year = iso ? first : last.length === 2 ? `20${last}` : last;
  const date = `${year}-${month.padStart(2, "0")}-${(iso ? last : first).padStart(2, "0")}`;
  const parsed = new Date(`${date}T00:00:00.000Z`);
  return year !== "0000" && Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date;
}

export function ocrDescriptionForItemMatch(value) {
  return String(value ?? "").trim().split(/\r\n?|\n/).map((line) => {
    const product = line.replace(traceField, (match, separator, date) =>
      date && !validPrintedDate(date) ? match : " ");
    return product === line ? line : product.replace(/^[\s,;|]+|[\s,;|]+$/g, "").replace(/\s{2,}/g, " ");
  }).filter(Boolean).join("\n");
}

function selectedItemId(line) {
  return text(line?.suggested_item_id ?? line?.item_id ?? line?.matched_item_id ?? line?.suggested_item?.id);
}

// The caller must supply only items belonging to the currently selected supplier.
// Legacy suggestions have no acceptance threshold; their IDs, names and scores are not evidence.
export function findReliableOcrItemForLine(line, items = []) {
  const id = selectedItemId(line);
  const selected = id ? items.find((item) => String(item.id) === id) : null;
  const suggestion = line?.suggested_item;
  const trustedSuggestion = suggestion?.match_policy === "conservative_v1"
    && reliableSources.has(suggestion.source) && text(suggestion.id) === id;
  if (selected && (line?._itemSelectionConfirmed === true || trustedSuggestion)) return selected;

  const description = ocrItemAliasKey(text(line?.description) || line?.ocr_description);
  const reference = referenceKey(line?.supplier_code);
  const nameMatches = description ? items.filter((item) => ocrItemAliasKey(item.name) === description) : [];
  const referenceMatches = reference ? items.filter((item) => [
    item.supplier_code,
    ...(Array.isArray(item.item_suppliers) ? item.item_suppliers.map((link) => link?.supplier_code) : []),
  ].some((value) => referenceKey(value) === reference)) : [];

  if (nameMatches.length > 1 || referenceMatches.length > 1) return null;
  if (nameMatches.length && referenceMatches.length && String(nameMatches[0].id) !== String(referenceMatches[0].id)) return null;
  if (referenceMatches.length) {
    const item = referenceMatches[0];
    return incompatible(nameParts(description), nameParts(ocrItemAliasKey(item.name))) ? null : item;
  }
  if (nameMatches.length) return nameMatches[0];
  const aliases = description ? items.filter((item) =>
    Array.isArray(item.ocr_item_aliases) && item.ocr_item_aliases.includes(description)) : [];
  return aliases.length === 1 ? aliases[0] : null;
}

export function buildSuggestedInternalCode(line, existingItems = []) {
  const source = text(line?.description) || text(line?.ocr_description);
  const base = source.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toUpperCase().replace(/[^A-Z0-9]+/g, "").slice(0, 12);
  if (!base) return "";
  const safeBase = /^[0-9]/.test(base) ? `ITM${base.slice(0, 9)}` : base;
  const existingCodes = new Set(existingItems.map((item) => referenceKey(item?.code)));
  if (!existingCodes.has(safeBase)) return safeBase;
  for (let suffix = 2; suffix < 1000; suffix += 1) {
    const candidate = `${safeBase.slice(0, Math.max(12 - String(suffix).length, 4))}${suffix}`;
    if (!existingCodes.has(candidate)) return candidate;
  }
  return `${safeBase.slice(0, 8)}${Date.now().toString().slice(-4)}`;
}
